import { useCallback, useMemo } from 'react';
import { useApiClient } from '@marageti/z4-lib';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { AgentWithUnreadCount, Conversation } from '@marageti/z4-sdk/lib/travel';
import { enqueueSnackbar } from 'notistack';
import useSWRInfinite from 'swr/infinite';
import useUnreadCount from '../../../hooks/use-unread-count';

const CONVERSATIONS_PAGE_SIZE = 30;

export default function useInboxConversations(agentPersonId: string | undefined, showUnreadOnly: boolean) {
  const apiClient = useApiClient();
  const { mutate: mutateUnreadCounts } = useUnreadCount(agentPersonId);
  const readUnreadConfig = useMemo(
    () => (agentPersonId ? { params: { person: agentPersonId } } : undefined),
    [agentPersonId],
  );

  const getKey = useCallback((pageIndex: number, previousPageData: SearchResult<Conversation> | null) => {
    if (!agentPersonId) return null;
    if (
      pageIndex > 0
      && previousPageData
      && previousPageData.hits.length < CONVERSATIONS_PAGE_SIZE
    ) return null;
    return [agentPersonId, showUnreadOnly, CONVERSATIONS_PAGE_SIZE, pageIndex] as const;
  }, [agentPersonId, showUnreadOnly]);

  const fetcher = useCallback(
    async ([agentId, hasNewOnly, pageSize, pageIndex]: readonly [
      string,
      boolean,
      number,
      number,
    ]) => {
      const result = await apiClient.conversationsClient.getParticipatingConversations({
        params: {
          page: pageIndex + 1,
          limit: pageSize,
          hasNewOnly,
          person: agentId,
        },
      });
      return result;
    },
    [apiClient],
  );

  const {
    data: pages,
    isLoading,
    isValidating,
    mutate: mutateParticipating,
    setSize,
  } = useSWRInfinite<SearchResult<Conversation>>(getKey, fetcher);

  const conversationsMap = useMemo(() => {
    const hits = (pages ?? []).flatMap((p) => p.hits ?? []);
    return hits.reduce<Record<string, Conversation>>(
      (acc, c) => ({
        ...acc,
        [c.id]: c,
      }),
      {},
    );
  }, [pages]);

  const totalHits = pages?.[0]?.totalHits ?? 0;
  const hasMoreConversations = totalHits > 0 && Object.keys(conversationsMap).length < totalHits;
  const handleLoadMoreConversations = useCallback(() => setSize((s) => s + 1), [setSize]);
  const isInitialLoad = (pages?.length ?? 0) <= 1;

  // Optimistically mark a conversation as read in the list and unread counts (My Inbox only). Does not call the server.
  const optimisticMarkConversationAsRead = useCallback((conversationId: string) => {
    if (!agentPersonId) return;
    const conv = (pages ?? []).flatMap((p) => p.hits ?? []).find((c) => c.id === conversationId);
    const wasUnread = conv?.messageState?.find((e) => e.personId === agentPersonId)?.hasNew ?? false;

    mutateParticipating((currentPages) => {
      if (!currentPages) return currentPages;
      return currentPages.map((page) => {
        const hits = (page.hits ?? []).map((c) => {
          if (c.id !== conversationId) return c;
          return {
            ...c,
            messageState: c.messageState?.map((entry) => (
              entry.personId === agentPersonId ? { ...entry, hasNew: false } : entry
            )) ?? [],
          };
        });
        return { ...page, hits };
      });
    }, { revalidate: false });

    if (wasUnread) {
      // Update unread count in the hook
      const updater = (currentAgents: AgentWithUnreadCount[] | undefined) => {
        if (!currentAgents) return currentAgents;
        return currentAgents.map((a) => (
          a.id === agentPersonId
            ? { ...a, unreadConversationCount: Math.max(0, (a.unreadConversationCount ?? 0) - 1) }
            : a
        ));
      };
      (mutateUnreadCounts as (u: typeof updater, o?: { revalidate?: boolean }) => void)(updater, { revalidate: false });
    }
  }, [agentPersonId, pages, mutateParticipating, mutateUnreadCounts]);

  const markConversationRead = useCallback((id: string) => {
    apiClient.conversationsClient.putConversationRead(id, readUnreadConfig)
      .then(() => enqueueSnackbar('Marked conversation as read', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to mark conversation as read', { variant: 'default' }))
      .finally(() => {
        mutateParticipating();
        mutateUnreadCounts();
      });
  }, [apiClient, readUnreadConfig, mutateParticipating, mutateUnreadCounts]);

  const markConversationUnread = useCallback((id: string) => {
    apiClient.conversationsClient.putConversationUnread(id, readUnreadConfig)
      .then(() => enqueueSnackbar('Marked conversation as unread', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to mark conversation as unread', { variant: 'default' }))
      .finally(() => {
        mutateParticipating();
        mutateUnreadCounts();
      });
  }, [apiClient, readUnreadConfig, mutateParticipating, mutateUnreadCounts]);

  return {
    conversationsMap,
    isLoading,
    isValidating,
    hasMoreConversations,
    handleLoadMoreConversations,
    isInitialLoad,
    markConversationRead,
    markConversationUnread,
    mutateParticipating,
    optimisticMarkConversationAsRead,
  };
}
